import { useContext, useEffect, useRef, useState } from "react";
import { Box, Grid, Typography, Button } from "@mui/material";
import { Helmet } from "react-helmet-async";
import RecurringBookingModal from "./RecurringBookingModal/RecurringBookingModal";
import RecurringPatientDataForm from "./RecurringPatientDataForm/RecurringPatientDataForm";
import {
  creaSvilTer,
  svilTerapia,
  valPren,
} from "../../networking";
import RecurringBookingMainForm from "./RecurringBookingMainForm/RecurringBookingMainForm";
import RecurringBookingFormDetail from "./RecurringBookingFormDetail/RecurringBookingFormDetail";
import LoadingProgressModal from "./LoadingProgressModal/LoadingProgressModal";
import { useFormRecurringBooking } from "./Context/formContext";
import dayjs from "dayjs";
import {
  RecurringBookingDataGenerated,
  RecurringBookingFormConfirm,
} from "../../services/recurringBookingInterfaces";
import { RecurringBookingContext } from "./Context/RecurringBookingContextWrapper";
import { AllertMessage } from "../DoctorsList/Medint/MedintDetails/MedintDetails";
import { IframeParams } from "../AppointmentManagement/Calendar/ModalReservation/ModalReservation";

const RecurringBookingManagement = () => {
  // Con AuthGuard, l'utente e' gia' autenticato quando raggiunge questa pagina

  const context = useContext(RecurringBookingContext);

  // generazione options selezionabili dal "dettaglio" di ciascuna form
  useEffect(() => {
    context?.requestEntiData();
    context?.requestPosizioniTicketData();
  }, []);

  // gestione logica di mapping degli appuntamenti selezionati dall'operatore
  useEffect(() => {
    context?.setConfermaPianoTerapiaBody(
      (prev: RecurringBookingFormConfirm) => {
        const updatedPrestazioni = [...(prev.prestazioni ?? [])];

        // cicla ogni prestazione per aggiornarne gli appuntamenti
        const newPrestazioni = updatedPrestazioni.map((prestazione, index) => {
          const appuntamenti = context?.selectedAppointments.filter(
            (app) => app.PROGRESSIVOPRESTAZIONE === String(index + 1)
          );

          return {
            ...prestazione,
            appuntamenti: (appuntamenti ?? []).map((e) => ({
              idAppuntamento: e.IDAPPUNTAMENTO,
              dataApp: e.DATA,
              oraApp: e.ORA,
              durata: e.DURATA.substring(10),
              codiceRisorsa: e.CODICERISORSA,
              descrisorsa: e.DESCRISORSA,
              codiceAgenda: e.CODICEAGENDA,
            })),
          };
        });

        return {
          ...prev,
          prestazioni: newPrestazioni,
        };
      }
    );
  }, [context?.selectedAppointments]);

  const [tabs, setTabs] = useState<number[]>([0]);
  const [submitRecurringFetchCall, setSubmitRecurringFetchCall] =
    useState<boolean>(false);
  const [viewState, setViewState] = useState<number>(0);
  const [viewSummary, setViewSummary] = useState<boolean>(false);
  const [viewAmount, setViewAmount] = useState<boolean>(false);
  const [numeroPrenotazione, setNumeroPrenotazione] = useState<string>("");
  const abortControllerRef = useRef<AbortController | null>(null);
  const [idSessioneReference, setIdSessioneReference] = useState<string>("");
  const [dataPrenotazione, setDataPrenotazione] = useState<string>("");

  // State per la gestione dell'iframe
  const [iframeStatus, setIframeStatus] = useState<IframeParams>({
      type: "",
      data: null,
      show: false,
    });

  // States per l'allert
  const [allert, setAllert] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [severity, setSeverity] = useState<string>("warning");

  const [pianoAppuntamenti, setPianoAppuntamenti] = useState<
    RecurringBookingDataGenerated[]
  >([]);

  const methods = useFormRecurringBooking();

  const handleAddTab = () => {
    setTabs((prev) => [...prev, prev.length]);
  };

  const handleRemoveTab = () => {
    setTabs((prev) => prev.slice(0, -1));
    handleResetLastInstance();
  };

  const handleResetLastInstance = () => {
    const lastIndex =
      methods.getValues("recurringBooking.istanzaSchedaDettaglio").length - 1;

    methods.resetField(`recurringBooking.istanzaSchedaDettaglio.${lastIndex}`);
  };

  const handleRicercaDisponibilitaSubmit = async () => {
    abortControllerRef.current = new AbortController();
    try {
      await handleLoadingRecurringData();
      await creaSvilTer({ signal: abortControllerRef.current.signal })
        .then((responseSessione) => {
          console.log(
            "idSessione creato con successo:",
            responseSessione.data.dati[0].idsessione
          );
          setIdSessioneReference(responseSessione.data.dati[0].idsessione);
          return responseSessione.data.dati[0].idsessione;
        })
        .then(async (idsessione: string) => {
          try {
            await svilTerapia({
              dataInizio: dayjs(
                methods.getValues("recurringBooking.dataInizioRicerca")
              ).format("YYYY-MM-DD"),
              idSessione: idsessione,
              orariAdiacenti:
                methods.getValues("recurringBooking.orariAdiacenti") ?? "0",
              tolleranzaMinuti:
                methods.getValues("recurringBooking.tolleranzaMinuti") ?? "15",
              giorniAdiacenti:
                methods.getValues("recurringBooking.giorniAdiacenti") ?? "0",
              orariFissi:
                methods.getValues("recurringBooking.orariFissi") ?? "0",
              sequenzaFissa:
                methods.getValues("recurringBooking.sequenzaFissa") ?? "0",
              risorsaFissa:
                methods.getValues("recurringBooking.risorsaFissa") ?? "0",
              giorniIntervallo:
                methods.getValues(
                  "recurringBooking.giorniIntervalloPrestazioni"
                ) ?? "0",
              giorniAbilitati: [
                methods.getValues("recurringBooking.giorniAbilitati.lunedi"),
                methods.getValues("recurringBooking.giorniAbilitati.martedi"),
                methods.getValues("recurringBooking.giorniAbilitati.mercoledi"),
                methods.getValues("recurringBooking.giorniAbilitati.giovedi"),
                methods.getValues("recurringBooking.giorniAbilitati.venerdi"),
                methods.getValues("recurringBooking.giorniAbilitati.sabato"),
                methods.getValues("recurringBooking.giorniAbilitati.domenica"),
              ],
              prestazioni: (
                methods.getValues("recurringBooking.istanzaSchedaDettaglio") ??
                []
              ).map((prest, index) => {
                return {
                  progressivo: index + 1,
                  specialita: prest.specialita.SPECIALITA || "",
                  prestazione: prest.prestazione.PRESTAZIONE || "",
                  tipologia: prest.tipologiaAgenda.CODICE || "P",
                  risorsa: prest.medico?.CODICE || "",
                  quantita: prest.numeroCicli || "1",
                  durata: prest.durata || "",
                  ora: prest.oraPrestazione || "",
                  distretto: prest.distretto || "",
                  posTicket:
                    "CODICEPOSTIC" in prest.ticket
                      ? prest.ticket.CODICEPOSTIC
                      : "",
                  ente: prest.ente?.codice || "",
                  speciaAbbinata: "",
                  prestAbbinata: "",
                };
              }),
            }).then((response_terapia) => {
              if (response_terapia.data.esito === "OK") {
                setDataPrenotazione(response_terapia.data.dataPrenotazione);
                if (abortControllerRef.current?.signal.aborted === false) {
                  setNumeroPrenotazione(response_terapia.data.numPrenotazione);
                  setPianoAppuntamenti(
                    response_terapia.data.appuntamenti[0].appuntamento
                  );

                  console.log(
                    "Sviluppo Terapia creato con successo:",
                    response_terapia.data.appuntamenti[0]
                  );
                }
                // Controllo per definire il warning dell'allert
                if (
                  !response_terapia.data.appuntamenti[0].appuntamento ||
                  response_terapia.data.appuntamenti?.[0]?.appuntamento
                    ?.length < 1
                ) {
                  setSeverity("warning");
                  setMessage(
                    "Nessuna disponibilità trovata"
                  );
                  setAllert(true);
                }
              }
            });
          } catch (error) {
            console.error("Errore durante la fetch svilTerapia:", error);
            setSeverity("error");
            setMessage("Si è verificato un errore nello sviluppo terapia");
            setAllert(true);
          } finally {
            console.log("AbortController", abortControllerRef.current);
            if (abortControllerRef.current?.signal.aborted === false) {
              handleLoadingRecurringData();
            }

              setTimeout(() => {
                setAllert(false);
              }, 5000);
          }
        });
    } catch (error) {
      console.error("Errore durante la fetch creaSvilTer:", error);
    }
  };

  type IstanzaDettaglio = {
    numeroCicli?: number | string;
    ticket?: { CODICEPOSTIC?: string; codice?: string; codicepostic?: string };
    ente?: { codice?: string; CODICE?: string; codiceEnte?: string };
    medico?: { CODICE?: string; CODICEMEDICO?: string };
    specialita?: { SPECIALITA?: string };
    prestazione?: { PRESTAZIONE?: string };
    tipologiaAgenda?: { CODICE?: string };
  };

  const handleAmountSummaryOpening = async () => {
    const istanzaDettaglio = (methods.getValues(
      "recurringBooking.istanzaSchedaDettaglio"
    ) ?? []) as IstanzaDettaglio[];

    const prestazioniOrig =
      context?.confermaPianoTerapiaBody?.prestazioni ?? [];

    // console.log({ prestazioniOrig });

    const payload = {
      prestazioni: prestazioniOrig.map((element: any, index: number) => {
        // console.log(istanzaDettaglio[index]);

        return {
          numImpegnativa: element.impegnativa ?? "",
          codPostic: istanzaDettaglio[index].ticket?.CODICEPOSTIC ?? "",
          dataImpegnativa: element.dataImpegnativa ?? "0001-01-01",
          dataPrenotazione: dataPrenotazione ?? "", // arriva dalla risposta dell'api di /svilTerapia
          // al momento HANNO richiesto di passare solo il primo risultato indicativamente.. in futuro probabilmente quando si presenterà loro l'esigenza chiederanno un array
          dataAppuntamento: element.appuntamenti[0].dataApp ?? "",
          // un campo che sembra non essere necessario valorizzare ma comunquer richiesto da passare a stringa vuota
          codConven: "",
          codEnte: istanzaDettaglio[index].ente?.codice ?? "",
          codTpgpre: element.tipologia ?? "",
          codSpecia: element.specialita ?? "",
          codAnapre: element.prestazione ?? "",
          quantita: element.appuntamenti.length.toString() ?? "0",
          // al momento HANNO richiesto di passare solo il primo risultato indicativamente.. in futuro probabilmente quando si presenterà loro l'esigenza chiederanno un array
          codMedint: element.appuntamenti[0].codiceRisorsa ?? "",
        };
      }),
    };

    const response = await valPren(payload);
    if (response.data.esito === "OK") {
      console.log("Importo a carico Paziente:", response.data);

      context?.setImportoListino(response.data || "");
      context?.setImportoTotale(response.data.totCaricoPaziente || "");
      setViewAmount(true);
    }
  };

  const handlePageReset = () => {
    methods.reset();
    context?.setSelectedAppointments([]);
    setViewState(0);
    setTabs([0]);
    setNumeroPrenotazione("");
    setPianoAppuntamenti([]);
    tabs.forEach((tabIndex) => {
      methods.resetField(
        `recurringBooking.istanzaSchedaDettaglio.${tabIndex}.specialita`
      );
      methods.resetField(
        `recurringBooking.istanzaSchedaDettaglio.${tabIndex}.prestazione`
      );
      methods.resetField(
        `recurringBooking.istanzaSchedaDettaglio.${tabIndex}.tipologiaAgenda`
      );
      methods.resetField(
        `recurringBooking.istanzaSchedaDettaglio.${tabIndex}.medico`
      );
      methods.resetField(
        `recurringBooking.istanzaSchedaDettaglio.${tabIndex}.numeroCicli`
      );
      methods.resetField(
        `recurringBooking.istanzaSchedaDettaglio.${tabIndex}.durata`
      );
      methods.resetField(
        `recurringBooking.istanzaSchedaDettaglio.${tabIndex}.oraPrestazione`
      );
      methods.resetField(
        `recurringBooking.istanzaSchedaDettaglio.${tabIndex}.distretto`
      );
      methods.resetField(
        `recurringBooking.istanzaSchedaDettaglio.${tabIndex}.ticket`
      );
      methods.resetField(
        `recurringBooking.istanzaSchedaDettaglio.${tabIndex}.ente`
      );
    });
  };

  const handleLoadingRecurringData = async () => {
    setSubmitRecurringFetchCall((prev) => !prev);
  };

  const handlePatientFormOpening = (value: number) => {
    setViewState(value);
  };

  const handleSummaryTableOpening = () => {
    setViewSummary(!viewSummary);
  };

  return (
    <>
      <>
        <LoadingProgressModal
          submitRecurringFetchCall={submitRecurringFetchCall}
          idSessioneReference={idSessioneReference}
          handleLoadingRecurringData={handleLoadingRecurringData}
          abortControllerRef={abortControllerRef}
        />
      </>
      <Helmet>
        <title>Prenotazioni Cicliche</title>
      </Helmet>
      <>
        <AllertMessage
          showAllert={allert}
          message={message}
          setShowAllert={setAllert}
          severity={severity}
        />
      </>
      <Box p={3} sx={{ backgroundColor: "#F8F8F8" }}>
        {viewState === 0 ? (
          <Grid container direction="column" spacing={2}>
            {/* Form principale */}
            <Grid item>
              <Typography variant="h6" gutterBottom>
                Prenotazioni Cicliche
              </Typography>
            </Grid>
            <Grid item>
              <RecurringBookingMainForm methods={methods} />
            </Grid>

            {/* Form Dati Prestazione */}
            <Grid item>
              <Box
                textAlign="right"
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <Typography variant="h6">Dati Prestazione</Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    methods?.handleSubmit((e) => {
                      console.log({ e });
                      setPianoAppuntamenti([]);
                      context?.setSelectedAppointments([]);
                      handleRicercaDisponibilitaSubmit();
                    })();
                  }}
                >
                  Ricerca Disponibilità
                </Button>
              </Box>
            </Grid>
            <Grid item>
              <Grid container spacing={2}>
                {tabs.map((id) => (
                  <Grid
                    item
                    key={id}
                    xs={
                      tabs.length === 1
                        ? 12
                        : tabs.length === 2
                        ? 6
                        : tabs.length === 3 && id === 2
                        ? 12
                        : 6
                    }
                  >
                    <RecurringBookingFormDetail
                      handleAddTab={handleAddTab}
                      handleRemoveTab={handleRemoveTab}
                      id={id}
                      tabs={tabs}
                      methods={methods}
                      pianoAppuntamenti={pianoAppuntamenti ?? []}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
            <RecurringBookingModal
              open={viewAmount}
              onClose={() => {
                setViewAmount(false);
              }}
            />
            <Grid item>
              <Grid container display={"flex"} justifyContent={"space-between"}>
                <Grid item>
                  <Button
                    sx={{ backgroundColor: "#fff !important" }}
                    variant="outlinedError"
                    onClick={handlePageReset}
                  >
                    Reset Appuntamenti
                  </Button>
                </Grid>
                <Grid item>
                  <Grid container spacing={2}>
                    {Array.isArray(pianoAppuntamenti) &&
                      pianoAppuntamenti.length > 0 && (
                        <>
                          <Grid item>
                            <Button
                              variant="outlined"
                              color="primary"
                              onClick={() => handleAmountSummaryOpening()}
                            >
                              Calcola Importo
                            </Button>
                          </Grid>
                          <Grid item>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() => handlePatientFormOpening(1)}
                            >
                              Conferma
                            </Button>
                          </Grid>
                        </>
                      )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        ) : (
          <RecurringPatientDataForm
            methods={methods}
            handlePatientFormOpening={handlePatientFormOpening}
            viewSummary={viewSummary}
            handleSummaryTableOpening={handleSummaryTableOpening}
            numeroPrenotazione={numeroPrenotazione}
            handlePageReset={handlePageReset}
            iframeStatus={iframeStatus}
            setIframeStatus={setIframeStatus}
          />
        )}
      </Box>
    </>
  );
};

export default RecurringBookingManagement;
